/**
 * Ted #1208 (Item 36) — Settings → Services & prices → Grooming menu.
 *
 * Ted (10-09) could not find where to set grooming prices. Johnny: "grooming
 * is only one service, but when a customer books grooming they're taken to a
 * follow-on screen with different options … that's not on the settings
 * page." This is that follow-on screen's source of truth, opened from the
 * Grooming row of Services & prices:
 *
 *   · Grooming services — each with a STARTING price (charged when the dog
 *     has no size on file) and a price per dog size (what is charged).
 *   · Add-ons — price, and "Clients can book this": off = staff-only (Ted's
 *     Walk $15, House Food $5/day), which the desk adds to a visit and the
 *     customer grooming menu never shows. And "This is the daily meal
 *     charge" (#1123): the add-on that IS the $5/day meal, so meal tracking
 *     never charges a day it is already on. A flag, not a name match:
 *     renaming House Food to "Kibble" keeps the day charged once.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: each service reads as one line of prices —
 *    starting price, then Small · Medium · Large · X-Large — from the same
 *    rows the customer quote and the charge read. A size with no price says
 *    "Starting price", never "$0" or a blank.
 *  - #2 The next action is obvious: per section one "Add …" action; per row
 *    "Edit prices" / "Edit" opens inline, Hide is a two-tap with the
 *    consequence spelled out, and the add-on visibility is ONE tap on a
 *    labelled checkbox. Non-owners see the menu with a plain reason.
 *  - #5 Never lie about data: a save reloads from the server before saying
 *    "saved"; a hidden row stays visible here (marked Hidden) so it can be
 *    offered again; staff-only is a word ("Staff only"), not a color.
 *  - #7 Built for hands: the size grid is two columns on a phone, five on
 *    desktop; every control is ≥ 44 px.
 *  - Five states: loading (skeleton) · error (+ Try again) · empty (per
 *    section, teaching copy + the add action) · ready · saving (per-row
 *    lock). Stale does not apply: this is the live menu, re-read on open.
 *
 * Counter Test: Ted opens Grooming → Grooming menu on his phone, changes
 * Large Bath & Trim to $135, taps Save; the next customer quote is $135.
 * He adds "Walk $15" with "Clients can book this" off; the desk can add it
 * to a visit and the customer grooming menu never shows it.
 *
 * Kate K9 (beta walkthrough, 2026-10-12): "dental brushing" beside
 * "toothbrushing", two deshedding entries — left from the owner's own edits,
 * because the server refuses only an exact match. A new or renamed service
 * or add-on whose name LOOKS like an existing one now gets a soft question
 * — "Looks like 'X' already exists; keep both?" — and the owner decides.
 * Never a block: two near names can both be real.
 *
 * Server contract: GET /api/v2/admin/tenant/grooming-menu; POST /services,
 * PATCH /services/:id, PUT /services/:id/prices, POST /add-ons, PATCH
 * /add-ons/:id (all business-config.pricing:write:all).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Checkbox, Input, Skeleton } from '@ferro/ui';
import {
  adminGroomingMenuApi,
  type GroomingMenuAddOn,
  type GroomingMenuService,
  type GroomingMenuView,
  type GroomingSizeKey,
  type GroomingSizePrices,
} from '../../lib/api.js';
import { centsToDollarInput, dollarInputToCents, formatDollars } from './capacity-helpers.js';
import { findNearDuplicateName, nearDuplicateQuestion } from '@ferro/core/modules/tenant-settings/service-name.js';

interface GroomingMenuEditorProps {
  apiUrl: string;
  /** Holds business-config.pricing:write:all — every write on this menu. */
  canEdit: boolean;
}

export const GROOMING_SIZE_ORDER: ReadonlyArray<{ key: GroomingSizeKey; label: string }> = [
  { key: 'small', label: 'Small' },
  { key: 'medium', label: 'Medium' },
  { key: 'large', label: 'Large' },
  { key: 'xl', label: 'X-Large' },
];

// ── Pure form helpers (exported for the component tests) ──────────────────

export interface GroomingServiceForm {
  displayName: string;
  description: string;
  startingPrice: string;
  isCoatRelated: boolean;
  /** Dollars per size; blank = no price for that size (the starting price is charged). */
  sizes: Record<GroomingSizeKey, string>;
}

export const EMPTY_SERVICE_FORM: GroomingServiceForm = {
  displayName: '',
  description: '',
  startingPrice: '',
  isCoatRelated: false,
  sizes: { small: '', medium: '', large: '', xl: '' },
};

export function serviceFormFrom(s: GroomingMenuService): GroomingServiceForm {
  return {
    displayName: s.displayName,
    description: s.description ?? '',
    startingPrice: centsToDollarInput(s.basePriceCents),
    isCoatRelated: s.isCoatRelated,
    sizes: {
      small: centsToDollarInput(s.prices.small),
      medium: centsToDollarInput(s.prices.medium),
      large: centsToDollarInput(s.prices.large),
      xl: centsToDollarInput(s.prices.xl),
    },
  };
}

export interface ParsedServiceForm {
  displayName: string;
  description: string | null;
  basePriceCents: number;
  isCoatRelated: boolean;
  prices: GroomingSizePrices;
}

export function parseServiceForm(f: GroomingServiceForm): { ok: true; value: ParsedServiceForm } | { ok: false; message: string } {
  const fail = (message: string) => ({ ok: false as const, message });
  const displayName = f.displayName.trim();
  if (displayName.length === 0 || displayName.length > 80) return fail('Give the grooming service a name customers will see (up to 80 characters).');
  const basePriceCents = dollarInputToCents(f.startingPrice);
  if (basePriceCents === null) return fail('Enter the starting price in dollars, for example 78 or 78.50.');
  const prices = { small: null, medium: null, large: null, xl: null } as GroomingSizePrices;
  for (const { key, label } of GROOMING_SIZE_ORDER) {
    const raw = f.sizes[key].trim();
    if (raw === '') continue;
    const cents = dollarInputToCents(raw);
    if (cents === null) return fail(`Enter the ${label} price in dollars, or leave it blank to charge the starting price.`);
    prices[key] = cents;
  }
  const description = f.description.trim();
  if (description.length > 500) return fail('Keep the description under 500 characters.');
  return { ok: true, value: { displayName, description: description === '' ? null : description, basePriceCents, isCoatRelated: f.isCoatRelated, prices } };
}

/** Only the sizes whose price moved — the PUT replaces exactly what it names. */
export function changedSizePrices(before: GroomingSizePrices, after: GroomingSizePrices): Partial<GroomingSizePrices> {
  const out: Partial<GroomingSizePrices> = {};
  for (const { key } of GROOMING_SIZE_ORDER) if (before[key] !== after[key]) out[key] = after[key];
  return out;
}

export interface GroomingAddOnForm {
  displayName: string;
  price: string;
  description: string;
  customerVisible: boolean;
  isMealCharge: boolean;
}

export const EMPTY_ADD_ON_FORM: GroomingAddOnForm = { displayName: '', price: '', description: '', customerVisible: true, isMealCharge: false };

export function parseAddOnForm(
  f: GroomingAddOnForm,
):
  | { ok: true; value: { displayName: string; priceCents: number; description: string | null; customerVisible: boolean; isMealCharge: boolean } }
  | { ok: false; message: string } {
  const displayName = f.displayName.trim();
  if (displayName.length === 0 || displayName.length > 80) return { ok: false, message: 'Give the add-on a name (up to 80 characters).' };
  const priceCents = dollarInputToCents(f.price);
  if (priceCents === null) return { ok: false, message: 'Enter the price in dollars, for example 15 or 5.00.' };
  const description = f.description.trim();
  if (description.length > 500) return { ok: false, message: 'Keep the description under 500 characters.' };
  return {
    ok: true,
    value: { displayName, priceCents, description: description === '' ? null : description, customerVisible: f.customerVisible, isMealCharge: f.isMealCharge },
  };
}

type Status = 'loading' | 'error' | 'ready';

export function GroomingMenuEditor({ apiUrl, canEdit }: GroomingMenuEditorProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [menu, setMenu] = useState<GroomingMenuView | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [confirmHideId, setConfirmHideId] = useState<string | null>(null);
  /** A service id, an add-on id, or 'new-service' / 'new-add-on'. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [serviceForm, setServiceForm] = useState<GroomingServiceForm>(EMPTY_SERVICE_FORM);
  const [addOnForm, setAddOnForm] = useState<GroomingAddOnForm>(EMPTY_ADD_ON_FORM);
  /** Kate K9 — a name that looks like an existing row's, waiting on the owner's answer. */
  const [dupWarning, setDupWarning] = useState<{ formId: string; existing: string } | null>(null);

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial'): Promise<boolean> => {
      if (mode === 'initial') {
        setStatus('loading');
        setLoadError(null);
      }
      const res = await adminGroomingMenuApi.get(apiUrl);
      if (res.error || !res.data) {
        if (mode === 'initial') {
          setLoadError(res.error ?? 'Could not load the grooming menu.');
          setStatus('error');
        }
        return false;
      }
      setMenu(res.data);
      setStatus('ready');
      return true;
    },
    [apiUrl],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const services = useMemo(() => menu?.services ?? [], [menu]);
  const addOns = useMemo(() => menu?.addOns ?? [], [menu]);

  const reset = () => {
    setRowError(null);
    setSaved(null);
    setConfirmHideId(null);
    setDupWarning(null);
  };

  const fail = (id: string, message: string) => {
    setBusyId(null);
    setRowError({ id, message });
  };

  const finish = async (message: string) => {
    await load('refresh');
    setBusyId(null);
    setEditingId(null);
    setSaved(message);
  };

  // ── Grooming services ────────────────────────────────────────────────
  const openService = (s: GroomingMenuService | null) => {
    reset();
    setEditingId(s ? s.id : 'new-service');
    setServiceForm(s ? serviceFormFrom(s) : EMPTY_SERVICE_FORM);
  };

  const saveService = async (s: GroomingMenuService | null, keepBoth = false) => {
    const id = s ? s.id : 'new-service';
    const parsed = parseServiceForm(serviceForm);
    if (!parsed.ok) return setRowError({ id, message: parsed.message });
    const v = parsed.value;
    // Kate K9 — a new or changed name that looks like another row asks once.
    if (!keepBoth && (!s || v.displayName !== s.displayName)) {
      const dup = findNearDuplicateName(v.displayName, services, s?.id);
      if (dup) return setDupWarning({ formId: id, existing: dup.displayName });
    }
    setDupWarning(null);
    setBusyId(id);
    setRowError(null);
    if (!s) {
      const res = await adminGroomingMenuApi.createService(apiUrl, {
        displayName: v.displayName,
        description: v.description,
        basePriceCents: v.basePriceCents,
        isCoatRelated: v.isCoatRelated,
        prices: v.prices,
      });
      if (res.error || !res.data) return fail(id, res.error ?? 'Could not add that grooming service.');
      return finish(`Added ${res.data.service.displayName}. Customers can pick it on their next grooming booking.`);
    }
    const patch: Record<string, unknown> = {};
    if (v.displayName !== s.displayName) patch.displayName = v.displayName;
    if (v.description !== (s.description ?? null)) patch.description = v.description;
    if (v.basePriceCents !== s.basePriceCents) patch.basePriceCents = v.basePriceCents;
    if (v.isCoatRelated !== s.isCoatRelated) patch.isCoatRelated = v.isCoatRelated;
    const sizes = changedSizePrices(s.prices, v.prices);
    if (Object.keys(patch).length === 0 && Object.keys(sizes).length === 0) {
      setBusyId(null);
      setEditingId(null);
      return;
    }
    if (Object.keys(patch).length > 0) {
      const res = await adminGroomingMenuApi.updateService(apiUrl, s.id, patch);
      if (res.error || !res.data) return fail(id, res.error ?? 'Could not save that grooming service.');
    }
    if (Object.keys(sizes).length > 0) {
      const res = await adminGroomingMenuApi.setServicePrices(apiUrl, s.id, sizes);
      if (res.error || !res.data) {
        await load('refresh');
        return fail(id, res.error ?? 'Could not save the size prices. Nothing else was lost — try Save again.');
      }
    }
    return finish(`${v.displayName}: saved. The next grooming booking is charged these prices.`);
  };

  const setServiceActive = async (s: GroomingMenuService, isActive: boolean) => {
    setBusyId(s.id);
    reset();
    const res = await adminGroomingMenuApi.updateService(apiUrl, s.id, { isActive });
    if (res.error || !res.data) return fail(s.id, res.error ?? 'Could not change that grooming service.');
    return finish(
      isActive
        ? `${s.displayName} is back on the grooming menu.`
        : `${s.displayName} is hidden from customers. Existing bookings are unaffected.`,
    );
  };

  // ── Add-ons ──────────────────────────────────────────────────────────
  const openAddOn = (a: GroomingMenuAddOn | null) => {
    reset();
    setEditingId(a ? a.id : 'new-add-on');
    setAddOnForm(
      a
        ? {
            displayName: a.displayName,
            price: centsToDollarInput(a.priceCents),
            description: a.description ?? '',
            customerVisible: a.customerVisible,
            isMealCharge: a.isMealCharge,
          }
        : EMPTY_ADD_ON_FORM,
    );
  };

  const saveAddOn = async (a: GroomingMenuAddOn | null, keepBoth = false) => {
    const id = a ? a.id : 'new-add-on';
    const parsed = parseAddOnForm(addOnForm);
    if (!parsed.ok) return setRowError({ id, message: parsed.message });
    const v = parsed.value;
    // Kate K9 — same soft question for add-ons ("dental brushing" vs "toothbrushing").
    if (!keepBoth && (!a || v.displayName !== a.displayName)) {
      const dup = findNearDuplicateName(v.displayName, addOns, a?.id);
      if (dup) return setDupWarning({ formId: id, existing: dup.displayName });
    }
    setDupWarning(null);
    setBusyId(id);
    setRowError(null);
    if (!a) {
      const res = await adminGroomingMenuApi.createAddOn(apiUrl, v);
      if (res.error || !res.data) return fail(id, res.error ?? 'Could not add that add-on.');
      return finish(
        v.customerVisible
          ? `Added ${v.displayName} at ${formatDollars(v.priceCents)}. Customers can pick it when they book grooming.`
          : `Added ${v.displayName} at ${formatDollars(v.priceCents)}, staff only. The desk can add it to a visit; customers never see it.`,
      );
    }
    const patch: Record<string, unknown> = {};
    if (v.displayName !== a.displayName) patch.displayName = v.displayName;
    if (v.priceCents !== a.priceCents) patch.priceCents = v.priceCents;
    if (v.description !== (a.description ?? null)) patch.description = v.description;
    if (v.customerVisible !== a.customerVisible) patch.customerVisible = v.customerVisible;
    if (v.isMealCharge !== a.isMealCharge) patch.isMealCharge = v.isMealCharge;
    if (Object.keys(patch).length === 0) {
      setBusyId(null);
      setEditingId(null);
      return;
    }
    const res = await adminGroomingMenuApi.updateAddOn(apiUrl, a.id, patch);
    if (res.error || !res.data) return fail(id, res.error ?? 'Could not save that add-on.');
    return finish(
      v.isMealCharge !== a.isMealCharge
        ? v.isMealCharge
          ? `${v.displayName}: saved. It is the daily meal charge now: meal tracking won't charge a day it is already on.`
          : `${v.displayName}: saved. It is no longer the meal charge: meal tracking charges its days as usual.`
        : `${v.displayName}: saved.`,
    );
  };

  /** One tap on "Clients can book this" is one PATCH. */
  const setAddOnVisible = async (a: GroomingMenuAddOn, customerVisible: boolean) => {
    setBusyId(a.id);
    reset();
    const res = await adminGroomingMenuApi.updateAddOn(apiUrl, a.id, { customerVisible });
    if (res.error || !res.data) return fail(a.id, res.error ?? 'Could not change who can book that add-on.');
    return finish(
      customerVisible
        ? `${a.displayName} is on the customer grooming menu.`
        : `${a.displayName} is staff only. The desk can still add it to a visit.`,
    );
  };

  const setAddOnActive = async (a: GroomingMenuAddOn, isActive: boolean) => {
    setBusyId(a.id);
    reset();
    const res = await adminGroomingMenuApi.updateAddOn(apiUrl, a.id, { isActive });
    if (res.error || !res.data) return fail(a.id, res.error ?? 'Could not change that add-on.');
    return finish(
      isActive
        ? `${a.displayName} is offered again.`
        : `${a.displayName} is hidden — customers and the desk stop seeing it. Existing bookings are unaffected.`,
    );
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <section className="ferro-groom-menu" aria-label="Grooming menu" data-testid="groom-menu-loading">
        <Skeleton variant="text" lines={6} />
      </section>
    );
  }

  if (status === 'error' || !menu) {
    return (
      <section className="ferro-groom-menu" aria-label="Grooming menu" data-testid="groom-menu-error">
        <Alert variant="error">{loadError ?? 'Could not load the grooming menu.'}</Alert>
        <div className="ferro-capacity__actions">
          <Button variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </div>
      </section>
    );
  }

  const errorFor = (id: string) =>
    rowError?.id === id ? (
      <div className="ferro-services__error" data-testid={`groom-menu-error-${id}`}>
        <Alert variant="error">{rowError.message}</Alert>
      </div>
    ) : null;

  /** Kate K9 — the soft warning block: the question, Keep both, Change the name. */
  const dupWarningFor = (id: string, onKeepBoth: () => void) =>
    dupWarning?.formId === id ? (
      <div className="ferro-services__dup" data-testid={`groom-dup-${id}`}>
        <Alert variant="warning">{nearDuplicateQuestion(dupWarning.existing)}</Alert>
        <div className="ferro-capacity__actions">
          <Button type="button" size="sm" variant="outline" isLoading={busyId === id} onClick={onKeepBoth} data-testid={`groom-dup-keep-${id}`}>
            Keep both
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={busyId === id} onClick={() => setDupWarning(null)} data-testid={`groom-dup-rename-${id}`}>
            Change the name
          </Button>
        </div>
      </div>
    ) : null;

  const serviceFormView = (s: GroomingMenuService | null) => {
    const id = s ? s.id : 'new-service';
    const busy = busyId === id;
    return (
      <form
        className="ferro-form ferro-groom-menu__form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void saveService(s);
        }}
        aria-label={s ? `Edit ${s.displayName}` : 'Add a grooming service'}
        data-testid={`groom-service-form-${id}`}
      >
        <div className="ferro-settings__form-grid">
          <Input
            id={`groom-service-name-${id}`}
            label="Name customers see"
            maxLength={80}
            placeholder="Bath & Trim"
            value={serviceForm.displayName}
            onChange={(e) => setServiceForm((f) => ({ ...f, displayName: e.target.value }))}
            data-testid={`groom-service-name-${id}`}
          />
          <Input
            id={`groom-service-start-${id}`}
            label="Starting price"
            type="text"
            inputMode="decimal"
            prefix="$"
            placeholder="78.00"
            value={serviceForm.startingPrice}
            onChange={(e) => setServiceForm((f) => ({ ...f, startingPrice: e.target.value }))}
            helperText="Charged when we don't know the dog's size, and shown as “from $…”."
            data-testid={`groom-service-start-${id}`}
          />
        </div>
        <fieldset className="ferro-groom-menu__sizes-edit">
          <legend className="ferro-groom-menu__legend">Price by dog size</legend>
          <p className="ferro-muted ferro-services__note">Leave a size blank to charge the starting price for it.</p>
          <div className="ferro-groom-menu__size-grid">
            {GROOMING_SIZE_ORDER.map(({ key, label }) => (
              <Input
                key={key}
                id={`groom-size-${id}-${key}`}
                label={label}
                type="text"
                inputMode="decimal"
                prefix="$"
                placeholder="Starting price"
                value={serviceForm.sizes[key]}
                onChange={(e) => setServiceForm((f) => ({ ...f, sizes: { ...f.sizes, [key]: e.target.value } }))}
                data-testid={`groom-size-${id}-${key}`}
              />
            ))}
          </div>
        </fieldset>
        <Input
          id={`groom-service-desc-${id}`}
          label="Description (optional)"
          maxLength={500}
          placeholder="Shampoo, blow-dry, nails and a tidy trim"
          value={serviceForm.description}
          onChange={(e) => setServiceForm((f) => ({ ...f, description: e.target.value }))}
          data-testid={`groom-service-desc-${id}`}
        />
        <Checkbox
          id={`groom-service-coat-${id}`}
          label="Ask about coat condition when booking"
          description="For haircuts and baths, where matting changes the work."
          checked={serviceForm.isCoatRelated}
          onChange={(e) => setServiceForm((f) => ({ ...f, isCoatRelated: e.target.checked }))}
          data-testid={`groom-service-coat-${id}`}
        />
        {errorFor(id)}
        {dupWarningFor(id, () => void saveService(s, true))}
        <div className="ferro-capacity__actions">
          <Button type="submit" size="sm" variant="primary" isLoading={busy} data-testid={`groom-service-save-${id}`}>
            {s ? 'Save' : 'Add grooming service'}
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setEditingId(null)}>
            Cancel
          </Button>
        </div>
      </form>
    );
  };

  const addOnFormView = (a: GroomingMenuAddOn | null) => {
    const id = a ? a.id : 'new-add-on';
    const busy = busyId === id;
    return (
      <form
        className="ferro-form ferro-groom-menu__form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void saveAddOn(a);
        }}
        aria-label={a ? `Edit ${a.displayName}` : 'Add an add-on'}
        data-testid={`groom-addon-form-${id}`}
      >
        <div className="ferro-settings__form-grid">
          <Input
            id={`groom-addon-name-${id}`}
            label="Name"
            maxLength={80}
            placeholder="Nail trim"
            value={addOnForm.displayName}
            onChange={(e) => setAddOnForm((f) => ({ ...f, displayName: e.target.value }))}
            data-testid={`groom-addon-name-${id}`}
          />
          <Input
            id={`groom-addon-price-${id}`}
            label="Price"
            type="text"
            inputMode="decimal"
            prefix="$"
            placeholder="15.00"
            value={addOnForm.price}
            onChange={(e) => setAddOnForm((f) => ({ ...f, price: e.target.value }))}
            data-testid={`groom-addon-price-${id}`}
          />
        </div>
        <Input
          id={`groom-addon-desc-${id}`}
          label="Description (optional)"
          maxLength={500}
          placeholder="Per day"
          value={addOnForm.description}
          onChange={(e) => setAddOnForm((f) => ({ ...f, description: e.target.value }))}
          data-testid={`groom-addon-desc-${id}`}
        />
        <Checkbox
          id={`groom-addon-visible-${id}`}
          label="Clients can book this"
          description="Off = staff only: the desk adds it to a visit, and it never shows on the customer grooming menu."
          checked={addOnForm.customerVisible}
          onChange={(e) => setAddOnForm((f) => ({ ...f, customerVisible: e.target.checked }))}
          data-testid={`groom-addon-visible-${id}`}
        />
        <Checkbox
          id={`groom-addon-meal-${id}`}
          label="This is the daily meal charge"
          description="Turn on for House Food, whatever you call it. When meal tracking is on, a day this add-on is already on is not charged a meal again."
          checked={addOnForm.isMealCharge}
          onChange={(e) => setAddOnForm((f) => ({ ...f, isMealCharge: e.target.checked }))}
          data-testid={`groom-addon-meal-${id}`}
        />
        {errorFor(id)}
        {dupWarningFor(id, () => void saveAddOn(a, true))}
        <div className="ferro-capacity__actions">
          <Button type="submit" size="sm" variant="primary" isLoading={busy} data-testid={`groom-addon-save-${id}`}>
            {a ? 'Save' : 'Add add-on'}
          </Button>
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setEditingId(null)}>
            Cancel
          </Button>
        </div>
      </form>
    );
  };

  const hideControls = (id: string, name: string, isActive: boolean, onHide: () => void, onShow: () => void, note: string) => {
    const busy = busyId === id;
    if (!isActive) {
      return (
        <Button size="sm" variant="ghost" isLoading={busy} onClick={onShow} data-testid={`groom-show-${id}`}>
          Offer again
        </Button>
      );
    }
    if (confirmHideId === id) {
      return (
        <>
          <Button size="sm" variant="danger" isLoading={busy} onClick={onHide} data-testid={`groom-hide-confirm-${id}`} aria-describedby={`groom-hide-note-${id}`}>
            Hide {name}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmHideId(null)}>
            Keep
          </Button>
          <p id={`groom-hide-note-${id}`} className="ferro-muted ferro-services__note" data-testid={`groom-hide-note-${id}`}>
            {note}
          </p>
        </>
      );
    }
    return (
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setConfirmHideId(id); setSaved(null); }} data-testid={`groom-hide-${id}`}>
        Hide
      </Button>
    );
  };

  return (
    <section className="ferro-groom-menu" aria-labelledby="groom-menu-title" data-testid="groom-menu-ready">
      <div className="ferro-groom-menu__head">
        <h3 id="groom-menu-title" className="ferro-groom-menu__title">
          Grooming menu
        </h3>
        <p className="ferro-muted ferro-services__note">
          What customers pick after they choose Grooming, and what each size pays. The booking page and the charge follow these prices.
        </p>
      </div>

      {saved ? (
        <div data-testid="groom-menu-saved">
          <Alert variant="success">{saved}</Alert>
        </div>
      ) : null}

      {/* ── Grooming services ── */}
      <div className="ferro-groom-menu__section">
        <div className="ferro-groom-menu__section-head">
          <h4 className="ferro-groom-menu__subtitle">Grooming services</h4>
          {canEdit && editingId !== 'new-service' ? (
            <Button size="sm" variant="outline" onClick={() => openService(null)} data-testid="groom-service-add">
              Add a grooming service
            </Button>
          ) : null}
        </div>
        {editingId === 'new-service' ? serviceFormView(null) : null}
        {services.length === 0 && editingId !== 'new-service' ? (
          <p className="ferro-groom-menu__empty" data-testid="groom-services-empty">
            No grooming services yet. {canEdit ? 'Add the first one customers will pick — for example Bath & Trim — with its price for each dog size.' : 'An owner adds them here.'}
          </p>
        ) : (
          <ul className="ferro-groom-menu__list" data-testid="groom-services-list">
            {services.map((s) => (
              <li
                key={s.id}
                className={`ferro-groom-menu__item${s.isActive ? '' : ' ferro-groom-menu__item--hidden'}`}
                data-testid={`groom-service-${s.id}`}
              >
                <div className="ferro-groom-menu__item-head">
                  <span className="ferro-groom-menu__name">{s.displayName}</span>
                  {s.isActive ? null : <Badge variant="closed">Hidden</Badge>}
                </div>
                {s.description ? <p className="ferro-muted ferro-services__note">{s.description}</p> : null}
                <dl className="ferro-groom-menu__prices" data-testid={`groom-service-prices-${s.id}`}>
                  <div className="ferro-groom-menu__price">
                    <dt>Starting at</dt>
                    <dd>{formatDollars(s.basePriceCents)}</dd>
                  </div>
                  {GROOMING_SIZE_ORDER.map(({ key, label }) => (
                    <div key={key} className="ferro-groom-menu__price" data-testid={`groom-service-price-${s.id}-${key}`}>
                      <dt>{label}</dt>
                      <dd>{s.prices[key] === null ? <span className="ferro-muted">Starting price</span> : formatDollars(s.prices[key] as number)}</dd>
                    </div>
                  ))}
                </dl>
                {canEdit && editingId !== s.id ? (
                  <div className="ferro-capacity__actions">
                    <Button size="sm" variant="ghost" disabled={busyId === s.id} onClick={() => openService(s)} data-testid={`groom-service-edit-${s.id}`}>
                      Edit prices
                    </Button>
                    {hideControls(
                      s.id,
                      s.displayName,
                      s.isActive,
                      () => void setServiceActive(s, false),
                      () => void setServiceActive(s, true),
                      `Customers stop seeing ${s.displayName}. Existing bookings are unaffected, and you can offer it again any time.`,
                    )}
                  </div>
                ) : null}
                {editingId === s.id ? serviceFormView(s) : errorFor(s.id)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Add-ons ── */}
      <div className="ferro-groom-menu__section">
        <div className="ferro-groom-menu__section-head">
          <h4 className="ferro-groom-menu__subtitle">Add-ons</h4>
          {canEdit && editingId !== 'new-add-on' ? (
            <Button size="sm" variant="outline" onClick={() => openAddOn(null)} data-testid="groom-addon-add">
              Add an add-on
            </Button>
          ) : null}
        </div>
        <p className="ferro-muted ferro-services__note">
          Extras on a visit. “Staff only” ones are added by the desk and never shown to customers.
        </p>
        {editingId === 'new-add-on' ? addOnFormView(null) : null}
        {addOns.length === 0 && editingId !== 'new-add-on' ? (
          <p className="ferro-groom-menu__empty" data-testid="groom-addons-empty">
            No add-ons yet. {canEdit ? 'Add nail trims or teeth brushing for customers, or staff-only extras like a walk.' : 'An owner adds them here.'}
          </p>
        ) : (
          <ul className="ferro-groom-menu__list" data-testid="groom-addons-list">
            {addOns.map((a) => (
              <li
                key={a.id}
                className={`ferro-groom-menu__item${a.isActive ? '' : ' ferro-groom-menu__item--hidden'}`}
                data-testid={`groom-addon-${a.id}`}
              >
                <div className="ferro-groom-menu__item-head">
                  <span className="ferro-groom-menu__name">{a.displayName}</span>
                  <span className="ferro-groom-menu__amount" data-testid={`groom-addon-price-${a.id}`}>
                    {formatDollars(a.priceCents)}
                  </span>
                  <span data-testid={`groom-addon-who-${a.id}`}>
                    <Badge variant={a.customerVisible ? 'success' : 'staff'} className="ferro-groom-menu__badge">
                      {a.customerVisible ? 'Clients can book' : 'Staff only'}
                    </Badge>
                  </span>
                  {a.isMealCharge ? (
                    <span data-testid={`groom-addon-meal-badge-${a.id}`}>
                      <Badge variant="info" className="ferro-groom-menu__badge">
                        Meal charge
                      </Badge>
                    </span>
                  ) : null}
                  {a.isActive ? null : <Badge variant="closed">Hidden</Badge>}
                </div>
                {a.description ? <p className="ferro-muted ferro-services__note">{a.description}</p> : null}
                {canEdit && editingId !== a.id ? (
                  <>
                    <div className="ferro-capacity__actions ferro-groom-menu__row-actions">
                      <Checkbox
                        id={`groom-addon-toggle-${a.id}`}
                        label="Clients can book this"
                        checked={a.customerVisible}
                        disabled={busyId === a.id || !a.isActive}
                        onChange={(e) => void setAddOnVisible(a, e.target.checked)}
                        data-testid={`groom-addon-toggle-${a.id}`}
                      />
                      <Button size="sm" variant="ghost" disabled={busyId === a.id} onClick={() => openAddOn(a)} data-testid={`groom-addon-edit-${a.id}`}>
                        Edit
                      </Button>
                      {hideControls(
                        a.id,
                        a.displayName,
                        a.isActive,
                        () => void setAddOnActive(a, false),
                        () => void setAddOnActive(a, true),
                        `Customers and the desk stop seeing ${a.displayName}. Existing bookings are unaffected.`,
                      )}
                    </div>
                  </>
                ) : null}
                {editingId === a.id ? addOnFormView(a) : errorFor(a.id)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {canEdit ? null : (
        <p className="ferro-muted" data-testid="groom-menu-readonly-note">
          Only an owner can change grooming prices and add-ons.
        </p>
      )}
    </section>
  );
}
